import { useState } from "react";
import { Send, MessageCircle, Phone, MapPin, Clock } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { BRAND, SERVICES, buildWhatsAppLink } from "@/data/site";
import { toast } from "sonner";

export default function QuickQuoteForm() {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    service: "",
    message: "",
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) {
      toast.error("Lütfen ad ve telefon bilgilerinizi girin.");
      return;
    }
    const text =
      `Merhaba, Star Otogaz için teklif talebim:\n\n` +
      `Ad Soyad: ${form.name}\n` +
      `Telefon: ${form.phone}\n` +
      (form.service ? `Hizmet: ${form.service}\n` : "") +
      (form.message ? `Not: ${form.message}\n` : "");
    window.open(buildWhatsAppLink(text), "_blank");
    toast.success("WhatsApp'a yönlendiriliyorsunuz...");
  };

  return (
    <section
      id="teklif-al"
      data-testid="quick-quote-section"
      className="relative py-24 lg:py-32 overflow-hidden"
    >
      <div className="absolute inset-0 spotlight-blue opacity-50 pointer-events-none" />
      <div className="absolute top-0 left-0 w-[500px] h-[500px] rounded-full bg-gold/8 blur-[120px] pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-start">
        <div>
          <p className="text-gold uppercase tracking-[0.4em] text-xs mb-3">
            Hızlı Teklif
          </p>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] text-balance mb-6">
            <span className="text-gradient-silver">Birkaç saniyede </span>
            <span className="text-gradient-gold">fiyat teklifi alın.</span>
          </h2>
          <p className="text-white/65 mb-10 text-base max-w-md leading-relaxed">
            Formu doldurun, mesajınız doğrudan WhatsApp üzerinden bize iletilsin.
            Bilgileri inceleyip en kısa sürede dönüş yapalım.
          </p>

          <div className="space-y-5">
            <a
              href={`tel:${BRAND.phoneTel}`}
              className="flex items-center gap-4 p-4 rounded-xl glass hover:border-gold/40 transition-all group"
            >
              <div className="w-11 h-11 rounded-lg bg-gold/10 border border-gold/30 flex items-center justify-center group-hover:bg-gold/20 transition-colors">
                <Phone className="w-5 h-5 text-gold" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-white/55">
                  Telefon
                </div>
                <div className="font-semibold text-white">{BRAND.phone}</div>
              </div>
            </a>

            <div className="flex items-start gap-4 p-4 rounded-xl glass">
              <div className="w-11 h-11 rounded-lg bg-gold/10 border border-gold/30 flex items-center justify-center flex-shrink-0">
                <MapPin className="w-5 h-5 text-gold" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-white/55">
                  Adres
                </div>
                <div className="text-white/90 text-sm leading-snug">
                  {BRAND.address}
                </div>
              </div>
            </div>

            <div className="flex items-center gap-4 p-4 rounded-xl glass">
              <div className="w-11 h-11 rounded-lg bg-gold/10 border border-gold/30 flex items-center justify-center">
                <Clock className="w-5 h-5 text-gold" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-white/55">
                  Çalışma Saatleri
                </div>
                <div className="font-semibold text-white">
                  {BRAND.workingHours}
                </div>
              </div>
            </div>
          </div>
        </div>

        <form
          onSubmit={submit}
          data-testid="quick-quote-form"
          className="relative glass-strong rounded-2xl p-7 sm:p-9"
        >
          <div className="absolute -inset-px rounded-2xl bg-gradient-to-br from-gold/20 via-transparent to-inci-blue/20 -z-10 blur-xl" />

          <h3 className="font-display text-2xl font-bold text-white mb-7">
            Teklif Formu
          </h3>

          <div className="space-y-4">
            <div>
              <label className="text-xs uppercase tracking-wider text-white/55 mb-2 block">
                Ad Soyad *
              </label>
              <Input
                data-testid="quote-name"
                value={form.name}
                onChange={(e) => set("name", e.target.value)}
                placeholder="Adınız ve soyadınız"
                className="bg-white/[0.03] border-white/10 text-white h-12 placeholder:text-white/30 focus-visible:ring-gold focus-visible:border-gold"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-white/55 mb-2 block">
                Telefon *
              </label>
              <Input
                data-testid="quote-phone"
                value={form.phone}
                onChange={(e) => set("phone", e.target.value)}
                placeholder="0xxx xxx xx xx"
                type="tel"
                className="bg-white/[0.03] border-white/10 text-white h-12 placeholder:text-white/30 focus-visible:ring-gold focus-visible:border-gold"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-white/55 mb-2 block">
                Hizmet
              </label>
              <Select
                value={form.service}
                onValueChange={(v) => set("service", v)}
              >
                <SelectTrigger
                  data-testid="quote-service"
                  className="bg-white/[0.03] border-white/10 text-white h-12 placeholder:text-white/30 focus:ring-gold"
                >
                  <SelectValue placeholder="Hizmet seçiniz" />
                </SelectTrigger>
                <SelectContent className="bg-[#0a0a0a] border-white/10 text-white">
                  {SERVICES.map((s) => (
                    <SelectItem key={s.slug} value={s.title}>
                      {s.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-white/55 mb-2 block">
                Mesajınız
              </label>
              <Textarea
                data-testid="quote-message"
                value={form.message}
                onChange={(e) => set("message", e.target.value)}
                placeholder="Aracınız, model yılı, ek bilgi..."
                rows={4}
                className="bg-white/[0.03] border-white/10 text-white placeholder:text-white/30 focus-visible:ring-gold focus-visible:border-gold resize-none"
              />
            </div>

            <button
              type="submit"
              data-testid="quote-submit"
              className="w-full gradient-gold text-black font-semibold py-4 inline-flex items-center justify-center gap-2 hover:shadow-[0_0_30px_rgba(212,175,55,0.4)] transition-all"
            >
              <Send className="w-4 h-4" />
              WhatsApp ile Gönder
            </button>
            <p className="text-xs text-white/45 text-center">
              Form gönderildiğinde WhatsApp uygulamanız açılır.
            </p>
          </div>
        </form>
      </div>
    </section>
  );
}
